import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, CircleAlert, CircleOff } from "lucide-react";

import { CategoryDetail } from "~/components/engagement/planning/life-design/category-detail";
import { Button } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import { sharedCategoryByIdQueryOptions } from "~/lib/queries/categoryQuery";
import { useUserInfoForEngagement } from "~/lib/queries/engagementQuery";

/**
 * One category of life that the client shares with the engagement's coach. The
 * data function checks the coach's engagement with the client and the client's
 * sharing; when either fails, the page shows the empty state, never the data.
 */
export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId/vision/$categoryId",
)({
  staticData: { title: "Category of life" },
  component: ClientCategoryPage,
});

function ClientCategoryPage() {
  const { engagementId, categoryId } = Route.useParams();
  const {
    data: category,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery(sharedCategoryByIdQueryOptions(categoryId));
  const { data: userInfo } = useUserInfoForEngagement(engagementId);

  return (
    <div className="space-y-8">
      <Link
        to="/my-coaching/clients/programs/$engagementId/client-vision"
        params={{ engagementId }}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="size-4" />
        Client's vision
      </Link>

      {isLoading || isFetching ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : isError ? (
        <EmptyState
          icon={CircleAlert}
          title="Unable to load this category"
          description="Check your connection and try again."
        >
          <Button type="button" onClick={() => void refetch()}>
            Retry
          </Button>
        </EmptyState>
      ) : !category ? (
        <EmptyState
          icon={CircleOff}
          title="This category isn't shared with you"
          description="Your client may have stopped sharing it."
        />
      ) : (
        <CategoryDetail category={category} clientId={userInfo?.userId} />
      )}
    </div>
  );
}
